/**
 * Amaron calendar "Customize view" metric definitions.
 * Edit this file to add, remove, or relabel metrics per module — no changes needed elsewhere.
 */
import type {
    CalendarDisplayMetricOption,
    CalendarDisplayMetricsConfig,
    CalendarDisplayMetricVisibilityContext,
} from "~/domainModels/calendarDisplayMetrics/types";
import {
    AMARON__EVENT__CALLS__CANCELLED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID,
    AMARON__EVENT__CALLS__LOGGED_ON__COLUMN_ID,
    AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
    AMARON__EVENT__SERVICE__BOOKED_ON__COLUMN_ID,
    AMARON__EVENT__SERVICE__CANCELLED_ON__COLUMN_ID,
    AMARON__EVENT__SERVICE__MECHANIC_ARRIVAL_TIME__COLUMN_ID,
    AMARON__EVENT__SERVICE__MECHANIC_DISPATCH_TIME__COLUMN_ID,
    AMARON__EVENT__SERVICE__SERVICE_COMPLETED_AT__COLUMN_ID,
    AMARON__EVENT__SERVICE__SERVICE_STARTED_AT__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SCHEDULED_CALL_TIME__COLUMN_ID,
    AmaronCallStatusEnum,
    AmaronEventTypes,
} from "~/domainModels/workspaceSpecific/amaron";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export type AmaronCalendarDisplayMetricsModule = "eventsCalls" | "eventsEnquiry" | "eventsService" | "journeysLeads" | "journeysInQueue";

export type AmaronCalendarDisplayMetricsContext = CalendarDisplayMetricVisibilityContext & {
    module: AmaronCalendarDisplayMetricsModule;
    eventTemplateId?: Uuid;
};

function systemMetric(id: string, label: string, columnId: "createdAt" | "updatedAt"): CalendarDisplayMetricOption {
    return {
        id: id,
        label: label,
        columnRef: {kind: "system", columnId: columnId},
    };
}

function eventColumnMetric(id: string, label: string, columnId: Uuid): CalendarDisplayMetricOption {
    return {
        id: id,
        label: label,
        columnRef: {kind: "eventAdditionalColumn", columnId: columnId},
    };
}

function journeyColumnMetric(id: string, label: string, columnId: Uuid): CalendarDisplayMetricOption {
    return {
        id: id,
        label: label,
        columnRef: {kind: "journeyAdditionalColumn", columnId: columnId},
    };
}

function isCallStatus(ctx: CalendarDisplayMetricVisibilityContext, status: Uuid): boolean {
    if (ctx.statusId === undefined || ctx.statusId === null) {
        return false;
    }
    return String(ctx.statusId) === String(status);
}

function resolveAmaronJourneyScheduledCallDateIso(row: unknown): string | undefined {
    const metricRow = row as {
        additionalColumnValues?: Record<Uuid, unknown>;
    };
    const values = metricRow.additionalColumnValues;
    if (values === undefined) {
        return undefined;
    }
    const dateRaw = values[AMARON__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID];
    if (dateRaw === null || dateRaw === undefined || String(dateRaw).trim() === "") {
        return undefined;
    }
    const dateStr = String(dateRaw).trim();
    if (Number.isNaN(Date.parse(dateStr)) === false) {
        return dateStr;
    }
    const timeRaw = values[AMARON__EVENT_CONTAINER__SCHEDULED_CALL_TIME__COLUMN_ID];
    if (timeRaw === null || timeRaw === undefined || String(timeRaw).trim() === "") {
        return undefined;
    }
    const combined = `${dateStr}T${String(timeRaw).trim()}`;
    if (Number.isNaN(Date.parse(combined)) === false) {
        return combined;
    }
    return undefined;
}

const AMARON_CALLS_CALENDAR_METRICS: CalendarDisplayMetricsConfig = {
    defaultMetricId: "createdOn",
    options: [
        systemMetric("createdOn", "Created on", "createdAt"),
        journeyColumnMetric("assignedOn", "Assigned on", AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID),
        systemMetric("updatedAt", "Updated at", "updatedAt"),
        eventColumnMetric("lastCallDate", "Last call date", AMARON__EVENT__CALLS__LAST_CALL_DATE__COLUMN_ID),
        {
            ...eventColumnMetric("scheduledOn", "Scheduled on", AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID),
            isVisible: (ctx) => isCallStatus(ctx, AmaronCallStatusEnum.LOGGED as Uuid) === false,
        },
        {
            ...eventColumnMetric("loggedOn", "Logged on", AMARON__EVENT__CALLS__LOGGED_ON__COLUMN_ID),
            isVisible: (ctx) => isCallStatus(ctx, AmaronCallStatusEnum.SCHEDULED as Uuid) === false,
        },
        eventColumnMetric("cancelledOn", "Cancelled on", AMARON__EVENT__CALLS__CANCELLED_ON__COLUMN_ID),
    ],
};

const AMARON_ENQUIRY_CALENDAR_METRICS: CalendarDisplayMetricsConfig = {
    defaultMetricId: "createdOn",
    options: [
        systemMetric("createdOn", "Created on", "createdAt"),
        journeyColumnMetric("assignedOn", "Assigned on", AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID),
        systemMetric("updatedAt", "Updated at", "updatedAt"),
    ],
};

const AMARON_SERVICE_CALENDAR_METRICS: CalendarDisplayMetricsConfig = {
    defaultMetricId: "createdOn",
    options: [
        systemMetric("createdOn", "Created on", "createdAt"),
        eventColumnMetric("serviceStartedAt", "Service started at", AMARON__EVENT__SERVICE__SERVICE_STARTED_AT__COLUMN_ID),
        eventColumnMetric(
            "serviceCompletedAt",
            "Service completed at",
            AMARON__EVENT__SERVICE__SERVICE_COMPLETED_AT__COLUMN_ID,
        ),
        eventColumnMetric("bookedOn", "Service booked on", AMARON__EVENT__SERVICE__BOOKED_ON__COLUMN_ID),
        eventColumnMetric("cancelledOn", "Cancelled on", AMARON__EVENT__SERVICE__CANCELLED_ON__COLUMN_ID),
        eventColumnMetric(
            "mechanicArrivalTime",
            "Mechanic arrival time",
            AMARON__EVENT__SERVICE__MECHANIC_ARRIVAL_TIME__COLUMN_ID,
        ),
        eventColumnMetric(
            "mechanicDispatchTime",
            "Mechanic dispatch time",
            AMARON__EVENT__SERVICE__MECHANIC_DISPATCH_TIME__COLUMN_ID,
        ),
        journeyColumnMetric("assignedOn", "Assigned on", AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID),
    ],
};

const AMARON_JOURNEY_LEADS_CALENDAR_METRICS: CalendarDisplayMetricsConfig = {
    defaultMetricId: "createdOn",
    options: [
        systemMetric("createdOn", "Created on", "createdAt"),
        {
            id: "scheduledOn",
            label: "Scheduled on",
            columnRef: {
                kind: "custom",
                filterColumnId: String(AMARON__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID),
                resolveIsoStart: resolveAmaronJourneyScheduledCallDateIso,
            },
        },
        journeyColumnMetric("lastActivityOn", "Last activity on", AMARON__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID),
        journeyColumnMetric("assignedOn", "Assigned on", AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID),
        systemMetric("updatedAt", "Updated at", "updatedAt"),
    ],
};

const AMARON_IN_QUEUE_CALENDAR_METRICS: CalendarDisplayMetricsConfig = {
    defaultMetricId: "createdOn",
    options: [
        systemMetric("modifiedOn", "Modified on", "updatedAt"),
        journeyColumnMetric("assignedOn", "Assigned on", AMARON__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID),
        systemMetric("createdOn", "Created on", "createdAt"),
    ],
};

const AMARON_CALENDAR_METRICS_BY_MODULE: Record<AmaronCalendarDisplayMetricsModule, CalendarDisplayMetricsConfig> = {
    eventsCalls: AMARON_CALLS_CALENDAR_METRICS,
    eventsEnquiry: AMARON_ENQUIRY_CALENDAR_METRICS,
    eventsService: AMARON_SERVICE_CALENDAR_METRICS,
    journeysLeads: AMARON_JOURNEY_LEADS_CALENDAR_METRICS,
    journeysInQueue: AMARON_IN_QUEUE_CALENDAR_METRICS,
};

export function resolveAmaronCalendarDisplayMetricsModuleFromEventTemplateId(
    eventTemplateId: Uuid,
): AmaronCalendarDisplayMetricsModule | undefined {
    if (eventTemplateId === (AmaronEventTypes.Calls as Uuid)) {
        return "eventsCalls";
    }
    if (eventTemplateId === (AmaronEventTypes.Enquiry as Uuid)) {
        return "eventsEnquiry";
    }
    if (eventTemplateId === (AmaronEventTypes.Service as Uuid)) {
        return "eventsService";
    }
    return undefined;
}

export function getAmaronCalendarDisplayMetrics(
    ctx: AmaronCalendarDisplayMetricsContext,
): CalendarDisplayMetricsConfig | undefined {
    return AMARON_CALENDAR_METRICS_BY_MODULE[ctx.module];
}
